import { useEffect, useMemo, useRef, useState } from 'react';
import Login from './componenents/login';
import Signup from './componenents/signup';
import Otp from './componenents/otp';
import NameStep from './componenents/name';
import UsernameStep from './componenents/username';
import PasswordStep from './componenents/password';
import BirthdayStep from './componenents/birthday';
import GenderStep from './componenents/gender';
import ProfileStep from './componenents/profile';
import Home from './componenents/pages/home';
import ShopMap from './componenents/pages/shopmap';
import MissionMap from './componenents/pages/map';
import Location from './componenents/pages/location';
import Discover from './componenents/pages/discover';
import Selfie from './componenents/pages/selfie';
import Camera from './componenents/pages/camera';
import Crop from './componenents/pages/crop';
import Post from './componenents/pages/post';
import Points from './componenents/pages/points';
import Journey from './componenents/pages/journey';
import Reward from './componenents/pages/reward';
import ScratchCard from './componenents/pages/scratchcard';
import RewardCards from './componenents/pages/rewardcards';
import MyProfile from './componenents/pages/myprofile';
import JourneysList from './componenents/pages/JourneysList';
import JourneyDetail from './componenents/pages/JourneyDetail';
import CityPicker from './componenents/pages/CityPicker';
import { getCompletedJourneyReward, mapRewardForUI, revealCompletedJourneyReward } from './services/rewardService';
import { getJourneys, getActiveJourneyForLocation } from './services/journeyService';
import { getStoredSelectedLocationId, storeSelectedLocationId } from './services/locationService';
import { getMissionStepsForJourney } from './services/missionStepService';
import { mapMissionStepsForUI } from './componenents/pages/missionStepAdapter';
import { getStoredUser, isAuthenticated } from './services/authService';
import { UNAUTHENTICATED_EVENT } from './services/apiClient';
import { getUserMissions, startUserMission, completeMissionStep, completeMissionPost, awardMissionPoint } from './services/userMissionService';
import { uploadSelfie } from './services/selfieService';
import { summarizeJourneysFromMissions } from './componenents/pages/journeyAdapter';

// Fallback step-number positions, used only until the selected journey's
// Laravel mission-step data has loaded (or if a step_key isn't among the
// journey's configured steps). Mirrors Step 5D's seeded step_number values.
// UI/bookkeeping-only — matches missionSteps.js's own documented
// ICON_BY_STEP_NUMBER/COLOR_BY_STEP_NUMBER fallback pattern and never
// fabricates mission-step content.
const DEFAULT_STEP_NUMBERS = { discover: 1, selfie: 2, post: 3, reward: 4 };

// Screens reached before the main app (Home, Login, and the standalone
// signup flow) — user_mission restoration (Step 5H) is never started while
// still on one of these, so it can never race the main Shop/Journey UI's
// own Admin-data fetch for the single-threaded local dev server. authUser
// is only ever set while still on 'home' (a restored session) or briefly on
// 'login' (auto-skipped) — never during signup, since that flow never sets
// authUser — but every one of these is listed for clarity/defensiveness.
const PRE_APP_VIEWS = new Set([
  'home', 'login', 'signup', 'otp', 'name', 'username', 'password', 'birthday', 'gender', 'profile', 'citypicker',
]);

// Step 6A: maps a user_mission's persisted current_step to the view it
// should resume at, so reusing an existing/restored mission never re-shows
// a step that's already been completed server-side.
//
// current_step 1 -> discover, 2 -> selfie, 3 -> post — matching exactly the
// step each one advances current_step to (progress()/SelfieController::
// store()/post()). current_step >= 4 means Discover/Selfie/Post are all
// already done, so Points is always the correct screen to land on next —
// this is true whether or not this specific mission already has its point
// transaction, or the journey is fully complete; either way there is
// nothing left to show on Discover/Selfie/Post. This function never derives
// or sets journey/point completion itself — that stays governed entirely by
// the existing journeyCompletedConfirmed/collectedStars restore effect,
// which already reads this same mission's persisted status/
// point_transactions once a journey is selected.
function getViewForMissionStep(mission) {
  const step = mission?.current_step;
  if (step === 2) return 'selfie';
  if (step === 3) return 'post';
  if (step >= 4) return 'points';
  return 'discover';
}

function App() {
  // Home is the first screen; Login is the second (reached from Home's "Start Mission").
  const [view, setView] = useState('home');

  // Dealzta is the source of truth for user accounts. authUser holds the
  // exact user object Dealzta returned on login (its user_id is never
  // regenerated), restored from localStorage on load if a prior session
  // exists.
  const [authUser, setAuthUser] = useState(() => getStoredUser());

  // If the Login screen is reached while a valid Dealzta session (token +
  // user) is already stored, skip straight past it instead of asking the
  // user to log in again. Never bypasses Login when either value is
  // missing.
  useEffect(() => {
    if (view === 'login' && isAuthenticated()) {
      setAuthUser(getStoredUser());
      setView(getStoredSelectedLocationId() ? 'shopmap' : 'citypicker');
    }
  }, [view]);

  // apiClient dispatches this whenever a SelfieBooth API request comes back
  // 401 (and has already cleared the stored Dealzta session). This just
  // resets local view state and returns to the existing Login screen — no
  // new UI, no local SelfieBooth login.
  useEffect(() => {
    const handleUnauthenticated = () => {
      setAuthUser(null);
      setView('login');
    };

    window.addEventListener(UNAUTHENTICATED_EVENT, handleUnauthenticated);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, handleUnauthenticated);
  }, []);

  // The authenticated user's own missions from Laravel (GET /user-missions,
  // Step 5H) — the single source used below to restore collectedStars /
  // journeyCompletedConfirmed / currentUserMissionId / shop.visited after a
  // refresh instead of leaving them reset to their empty defaults. null
  // while logged out or still loading; an array (possibly empty, a genuine
  // "no missions yet" state) once loaded. Cleared back to null on logout so
  // stale progress from a previous account is never shown to the next user.
  //
  // SECONDARY data: deliberately never a dependency of the main Shop/Journey
  // UI (ShopMap/JourneysList/JourneyDetail's own Admin-data fetches run
  // completely independently of this, both before and after). /user-missions
  // sits behind dealzta.auth, which makes its own synchronous call to
  // Dealzta's real /auth/me on every request; on the single-threaded local
  // `php artisan serve`, firing it immediately on mount (the original Step
  // 5H behavior) could queue every other in-flight request — including
  // ShopMap's /admin/shops — behind it. To avoid that, this request is
  // deferred until `view` has actually left the pre-app screens (Home/
  // Login/signup): React fires a newly-mounted child's effects (e.g.
  // ShopMap's getActiveShopsForJourney()) before its parent's effects from the same
  // commit, so by the time this fires, the main UI's own fetch has already
  // been dispatched.
  const [userMissions, setUserMissions] = useState(null);

  // Ensures the request fires exactly once per authenticated session — never
  // re-triggered by ordinary navigation (view keeps changing constantly
  // while the user moves through the app) and never by any restoration
  // output (userMissions/collectedStars/currentUserMissionId/
  // journeyCompletedConfirmed/selectedShop/selectedJourneyId are not, and
  // must not be, in this effect's dependency array).
  const missionsRequestedRef = useRef(false);

  useEffect(() => {
    if (!authUser || !isAuthenticated()) {
      setUserMissions(null);
      missionsRequestedRef.current = false;
      return undefined;
    }

    // Still on a pre-app screen — defer. This re-runs (deps include `view`)
    // as the user navigates further, so it naturally fires as soon as the
    // main app is actually reached, without polling or a timer.
    if (PRE_APP_VIEWS.has(view)) {
      return undefined;
    }

    if (missionsRequestedRef.current) {
      return undefined;
    }
    missionsRequestedRef.current = true;

    let cancelled = false;

    getUserMissions()
      .then((missions) => {
        if (!cancelled) setUserMissions(missions);
      })
      .catch((err) => {
        // Genuine-load-failure fallback ONLY: an empty array, never
        // fabricated/mock mission data. This mirrors the existing app's
        // "no data yet" state, so a failed restore behaves exactly like a
        // brand-new user with no missions rather than crashing or blocking
        // the normal mission-start flow from creating them as usual. The
        // Shop/Journey UI was already rendered before this ever started, so
        // a slow/failed restore never leaves it stuck.
        console.error('App: failed to load user missions from Laravel.', err);
        if (!cancelled) setUserMissions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [authUser, view]);

  // My Profile's "Current Journey" / "Completed Journeys" cards, derived
  // entirely from the restored userMissions above (Step 5H) — no separate
  // journeys API call, since each mission already carries its journey via
  // Laravel's eager-loaded relation.
  const { currentJourney, completedJourneys } = useMemo(
    () => summarizeJourneysFromMissions(userMissions),
    [userMissions]
  );

  const [pendingContact, setPendingContact] = useState({ method: 'phone', contact: '' });
  const [missionStep, setMissionStep] = useState(DEFAULT_STEP_NUMBERS.discover);
  const [selfiePhoto, setSelfiePhoto] = useState(null);
  const [photoToCrop, setPhotoToCrop] = useState(null);
  const [cropReturnView, setCropReturnView] = useState('selfie');
  const [selectedShop, setSelectedShop] = useState(null);
  const [pointsReturnView, setPointsReturnView] = useState('post');
  const [rewardCardsReturnView, setRewardCardsReturnView] = useState('selfie');
  const [profileReturnView, setProfileReturnView] = useState('shopmap');
  // No journey is selected until the user picks one from JourneysList/MyProfile.
  const [selectedJourneyId, setSelectedJourneyId] = useState(null);
  const [journeyDetailReturnView, setJourneyDetailReturnView] = useState('myprofile');

  // The city the user picked on CityPicker (shown once, right after login,
  // if none is stored yet) — restored from localStorage so a returning user
  // never has to pick again. '' means none chosen yet.
  const [selectedLocationId, setSelectedLocationId] = useState(() => getStoredSelectedLocationId());

  // The one journey Admin has marked live for selectedLocationId (Admin
  // enforces at most one active journey per location — see
  // JourneyController::deactivateSiblingJourneys), which is what ShopMap's
  // main trail is scoped to. undefined while unresolved (no location chosen
  // yet, or the lookup hasn't finished), null once resolved to "no live
  // journey for this city yet".
  const [locationJourneyId, setLocationJourneyId] = useState(undefined);

  // The chosen city's display name, read off that same resolved journey's
  // eager-loaded `location` (GET /admin/journeys already includes it) —
  // never a second API call. Shown top-left on ShopMap. null until resolved.
  const [selectedCityName, setSelectedCityName] = useState(null);

  useEffect(() => {
    if (!selectedLocationId) {
      setLocationJourneyId(undefined);
      setSelectedCityName(null);
      return undefined;
    }

    let cancelled = false;
    setLocationJourneyId(undefined);

    getActiveJourneyForLocation(selectedLocationId)
      .then((journey) => {
        if (cancelled) return;
        setLocationJourneyId(journey?.id ?? null);
        setSelectedCityName(journey?.location?.city ?? null);
      })
      .catch((err) => {
        console.error('App: failed to load the live journey for the selected city from Laravel.', err);
        if (cancelled) return;
        setLocationJourneyId(null);
        setSelectedCityName(null);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedLocationId]);
  // Points and Journey both read from this shared count.
  const [collectedStars, setCollectedStars] = useState(0);

  // Whether the backend has actually confirmed (via points()'s own
  // `journey_completed`, Step 5F) that the currently active journey is
  // complete — never derived locally from collectedStars/placesRequired.
  // The Reward screen is only reachable once this is true.
  const [journeyCompletedConfirmed, setJourneyCompletedConfirmed] = useState(false);

  // Reward unlock requirement, sourced from Admin (journeys.places_required)
  // for the currently selected journey — never hardcoded. null means "not
  // known yet" (no journey selected, or its data is still loading), and the
  // reward must never unlock while it's null.
  const [placesRequired, setPlacesRequired] = useState(null);

  useEffect(() => {
    if (selectedJourneyId == null) {
      setPlacesRequired(null);
      return undefined;
    }

    let cancelled = false;
    setPlacesRequired(null);

    getJourneys()
      .then((journeys) => {
        if (cancelled) return;
        const found = journeys.find((j) => j.id === selectedJourneyId);
        setPlacesRequired(found && typeof found.places_required === 'number' ? found.places_required : null);
      })
      .catch((err) => {
        console.error('App: failed to load places_required for selected journey from Laravel.', err);
        if (!cancelled) setPlacesRequired(null);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedJourneyId]);

  // Restore collectedStars / journeyCompletedConfirmed for the selected
  // journey from the authenticated user's own restored missions (Step 5H),
  // instead of leaving them at their post-refresh 0/false defaults.
  //
  // - collectedStars: counts missions in this journey whose point_transactions
  //   came back non-empty — the same signal points() itself uses to decide a
  //   completed shop. Never a mission count, never derived from current_step.
  // - journeyCompletedConfirmed: true only when one of this journey's
  //   missions already has status === 'completed', the exact flag Step 5F's
  //   points() persists once places_required is met. Never recomputed here
  //   from collectedStars/placesRequired — Laravel stays the sole source of
  //   truth for completion.
  //
  // Waits for userMissions to actually be loaded (not just selectedJourneyId
  // changing) so a value a live points()/awardMissionPoint response already
  // set in this same session is never clobbered by incomplete/absent data.
  useEffect(() => {
    if (selectedJourneyId == null || userMissions == null) return;

    const journeyMissions = userMissions.filter((mission) => mission.journey_id === selectedJourneyId);

    const completedShops = journeyMissions.filter(
      (mission) => Array.isArray(mission.point_transactions) && mission.point_transactions.length > 0
    ).length;

    setCollectedStars(completedShops);
    setJourneyCompletedConfirmed(journeyMissions.some((mission) => mission.status === 'completed'));
  }, [selectedJourneyId, userMissions]);

  // ShopMap-facing view of collectedStars, forced to 0 whenever no journey
  // is selected — `collectedStars` itself is only ever set/recomputed above
  // for a specific selectedJourneyId (never a global/all-journey count), but
  // this guard ensures ShopMap can never render a leftover value from a
  // previously selected journey during the brief window before a new
  // selection's restore effect (above) has run, or whenever selectedJourneyId
  // is genuinely null. Points/Journey are unaffected — they're only ever
  // reached once a journey is already selected, so they keep reading the
  // underlying collectedStars directly, unchanged.
  const shopMapCollectedStars = selectedJourneyId == null ? 0 : collectedStars;

  // Mission-step configuration for the selected journey (Admin is the
  // source of truth via journey_mission_steps + mission_steps). Used to
  // derive each step_key's step_number so App.js's step tracking follows
  // Admin config instead of hardcoded literals. null = not loaded yet
  // (or no journey selected); [] is a genuine "no steps configured" result
  // and is never overridden with fallback/hardcoded data.
  const [journeyMissionSteps, setJourneyMissionSteps] = useState(null);

  useEffect(() => {
    if (selectedJourneyId == null) {
      setJourneyMissionSteps(null);
      return undefined;
    }

    let cancelled = false;
    setJourneyMissionSteps(null);

    getMissionStepsForJourney(selectedJourneyId)
      .then((steps) => {
        if (!cancelled) setJourneyMissionSteps(mapMissionStepsForUI(steps));
      })
      .catch((err) => {
        console.error('App: failed to load mission steps for selected journey from Laravel.', err);
        if (!cancelled) setJourneyMissionSteps([]);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedJourneyId]);

  // step_key -> step_number, built from the loaded journey mission steps.
  // getStepNumber falls back to DEFAULT_STEP_NUMBERS only when a key isn't
  // present yet (still loading, or genuinely not configured for this
  // journey) — it never overrides a step_number that Laravel did return.
  const stepNumberByKey = useMemo(() => {
    return new Map(
      (journeyMissionSteps || [])
        .filter((step) => step.stepKey && typeof step.stepNumber === 'number')
        .map((step) => [step.stepKey, step.stepNumber])
    );
  }, [journeyMissionSteps]);

  const getStepNumber = (stepKey) => stepNumberByKey.get(stepKey) ?? DEFAULT_STEP_NUMBERS[stepKey];

  const completeJourney = () => {
    setCollectedStars((currentStars) =>
      placesRequired == null ? currentStars + 1 : Math.min(currentStars + 1, placesRequired)
    );
  };

  // The authenticated user's backend user_mission id for the currently
  // selected shop — the real, server-issued id every progress call below
  // targets. Never a hardcoded/guessed id. null until startUserMission
  // resolves (or if it fails), in which case progress calls are skipped
  // rather than sent against a made-up id.
  const [currentUserMissionId, setCurrentUserMissionId] = useState(null);

  // Report the Post step finishing (the dedicated /post endpoint — never
  // the generic /progress one used for Discover), then collect this shop's
  // +1 point — the backend is the sole source of truth for both. The UI
  // only advances to Points (and collectedStars only updates) once the
  // backend confirms the point; on any failure along the way, nothing is
  // shown as awarded and the user stays on Post so they can retry, matching
  // the existing guard pattern used by Discover/Crop above. Points are
  // never requested if Post completion itself fails.
  const completePostStepAndAwardPoint = () => {
    if (currentUserMissionId == null) {
      window.alert('Your mission is not ready yet. Please go back and try again.');
      return;
    }

    completeMissionPost(currentUserMissionId)
      .then(() => awardMissionPoint(currentUserMissionId))
      .then((result) => {
        const completedShops = result?.progress?.completed_shops;
        if (typeof completedShops === 'number') {
          setCollectedStars(completedShops);
        } else {
          // journey_id wasn't set on the mission (no progress to report) —
          // fall back to the existing local-increment behavior rather than
          // leaving collectedStars stale.
          completeJourney();
        }

        // Reflect the point transaction points() just recorded on this
        // mission inside local userMissions state right away. The Points
        // screen's Star Badge count (collectedShops) is computed purely
        // from userMissions' point_transactions arrays — without this, that
        // screen kept showing the pre-award snapshot (stuck at 0) until a
        // full reload, even though the backend had already awarded the
        // point. points()'s own user_mission response doesn't eager-load
        // point_transactions, so the returned point_transaction is merged
        // in by hand instead of trusting that field.
        //
        // Upsert, not just update: a mission started fresh this session
        // (selectShopForMission's startUserMission path) was never appended
        // to userMissions — only the initial GET /user-missions restore and
        // this block itself ever populate that array. Mapping-only left
        // brand-new-this-session missions with no matching id to update, so
        // the count silently stayed stuck at 0 for exactly the case a first-
        // time visitor hits: their very first booth in a session.
        if (result?.point_transaction) {
          setUserMissions((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const index = list.findIndex((mission) => mission.id === currentUserMissionId);
            const baseMission = index === -1 ? {} : list[index];
            const mergedMission = {
              ...baseMission,
              ...result.user_mission,
              point_transactions: [
                ...(Array.isArray(baseMission.point_transactions) ? baseMission.point_transactions : []),
                result.point_transaction,
              ],
            };

            if (index === -1) {
              return [...list, mergedMission];
            }

            const next = list.slice();
            next[index] = mergedMission;
            return next;
          });
        }

        // The backend's own points() response (Step 5F) is the sole source
        // of truth for journey completion — never recomputed here from
        // collectedStars/placesRequired.
        if (result?.progress?.journey_completed === true) {
          setJourneyCompletedConfirmed(true);
        }
        setMissionStep(getStepNumber('reward'));
        setPointsReturnView('post');
        setView('points');
      })
      .catch((err) => {
        console.error('App: failed to report post-step completion / award point.', err);
        window.alert(err.message || 'Could not confirm your mission completion. Please try again.');
      });
  };

  // Entry point for every "start this shop's mission" action (ShopMap and
  // JourneyDetail). Laravel shop records carry journey_id, so this is how
  // selectedJourneyId — and therefore placesRequired — gets populated when a
  // mission is started directly from ShopMap instead of via JourneysList.
  // If the shop has no journey_id (e.g. JourneyDetail's mapped shop objects,
  // where selectedJourneyId is already set from the journey the user picked),
  // the existing selectedJourneyId is used as the fallback instead of being
  // invented.
  const selectShopForMission = (shop) => {
    setSelectedShop(shop);
    const journeyId = shop?.journey_id ?? selectedJourneyId;
    if (journeyId != null) {
      setSelectedJourneyId(journeyId);
    }

    // Reuse the mission already restored from Laravel (Step 5H) for this
    // shop, if one was loaded via GET /user-missions, instead of calling
    // /start again. startUserMission is itself idempotent (backend
    // firstOrCreate + a unique user+shop constraint), so this is purely an
    // optimization/consistency measure, not a correctness requirement — but
    // it means a returning user's real mission id is used immediately
    // rather than waiting on a network round trip that can only ever
    // resolve to the same row.
    const restoredMission = (userMissions || []).find((mission) => mission.shop_id === shop?.id);

    if (restoredMission) {
      setCurrentUserMissionId(restoredMission.id);
      // Step 6A: resume at the step this mission has actually reached
      // server-side (current_step), instead of always re-showing Discover
      // for a mission that's already partway (or fully) done.
      const resumeView = getViewForMissionStep(restoredMission);
      if (resumeView === 'points') {
        setMissionStep(getStepNumber('reward'));
        setPointsReturnView('post');
      }
      setView(resumeView);
      return;
    }

    // Resolves (creates or idempotently retrieves) this user's backend
    // mission for the chosen shop so the step-completion calls below have a
    // real mission id. Never blocks navigation — the existing Discover
    // screen still opens immediately regardless of this call's outcome;
    // a failure is only logged, matching the app's existing pattern for
    // background data loads.
    setCurrentUserMissionId(null);
    if (journeyId != null && shop?.id != null) {
      startUserMission(journeyId, shop.id)
        .then((mission) => {
          setCurrentUserMissionId(mission?.id ?? null);
          // Step 6A: startUserMission is idempotent — if it actually
          // resolved to an existing, further-along mission (e.g. this shop's
          // mission wasn't present in the userMissions restore snapshot yet),
          // correct the optimistic 'discover' view set below to match its
          // real progress instead of leaving the user stuck re-doing a
          // completed step.
          const resumeView = getViewForMissionStep(mission);
          if (resumeView !== 'discover') {
            if (resumeView === 'points') {
              setMissionStep(getStepNumber('reward'));
              setPointsReturnView('post');
            }
            setView(resumeView);
          }
        })
        .catch((err) => {
          console.error('App: failed to start/retrieve user mission from Laravel.', err);
        });
    }

    // A newly-created mission naturally starts at Discover — shown
    // immediately/optimistically rather than waiting on the network call
    // above (corrected afterward if that call reveals otherwise).
    setView('discover');
  };

  // The single reward for the authenticated user's own backend-verified
  // completed journey (Step 5G) — shared by the direct Points -> Reward ->
  // ScratchCard path and by RewardCards. Resolved entirely server-side via
  // getCompletedJourneyReward(); selectedJourneyId is never used as proof
  // of completion and no reward list is ever fetched/filtered here.
  const [journeyReward, setJourneyReward] = useState(null);
  const [journeyRewardStatus, setJourneyRewardStatus] = useState('idle'); // idle | loading | ready | empty | error

  useEffect(() => {
    if (view !== 'reward' && view !== 'scratch' && view !== 'rewardcards') return undefined;

    let cancelled = false;
    setJourneyRewardStatus('loading');

    getCompletedJourneyReward()
      .then((reward) => {
        if (cancelled) return;
        setJourneyReward(reward ? mapRewardForUI(reward) : null);
        setJourneyRewardStatus(reward ? 'ready' : 'empty');
      })
      .catch((err) => {
        console.error('App: failed to load reward for the completed journey from Laravel.', err);
        if (cancelled) return;
        setJourneyReward(null);
        setJourneyRewardStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [view]);

  let content;

  if (view === 'login') {
    content = (
      <Login
        onLoginSuccess={(user) => {
          setAuthUser(user ?? getStoredUser());
          setView(getStoredSelectedLocationId() ? 'shopmap' : 'citypicker');
        }}
      />
    );
  } else if (view === 'citypicker') {
    content = (
      <CityPicker
        onSelectLocation={(locationId) => {
          storeSelectedLocationId(locationId);
          setSelectedLocationId(locationId);
          setView('shopmap');
        }}
      />
    );
  } else if (view === 'shopmap') {
    content = (
      <ShopMap
        journeyId={locationJourneyId}
        cityName={selectedCityName}
        onBack={() => setView('login')}
        onJourney={() => {
          // Step 6F/6G: Points needs a journey context to scope its Star
          // Badge count. Unlike selectShopForMission, this entry point isn't
          // tied to a specific shop, so selectedJourneyId can still be null
          // here (e.g. right after reload/re-login, before any shop is
          // clicked this session). Reuse the existing journey summaries
          // already derived from userMissions (summarizeJourneysFromMissions,
          // the same ones MyProfile shows) instead of inventing a new
          // selection rule:
          //   - currentJourney: the still-in-progress journey (Step 6F).
          //   - completedJourneys[0]: Step 6G — currentJourney intentionally
          //     excludes a journey once it hits 100%, so once the user's
          //     journey is fully completed it only exists in
          //     completedJourneys; without this fallback the Achievement
          //     screen would lose its journey context the moment the last
          //     shop is finished. completedJourneys[0] is unambiguous for a
          //     user with a single completed journey; with no unambiguous
          //     journey at all, selectedJourneyId is simply left as-is
          //     rather than guessed.
          if (selectedJourneyId == null) {
            const resolvedJourneyId = currentJourney?.id ?? completedJourneys[0]?.id ?? null;
            if (resolvedJourneyId != null) {
              setSelectedJourneyId(resolvedJourneyId);
            }
          }
          setPointsReturnView('shopmap');
          setView('points');
        }}
        onReward={() => {
          setRewardCardsReturnView('shopmap');
          setView('rewardcards');
        }}
        onSample={() => setView('location')}
        onProfile={() => {
          setProfileReturnView('shopmap');
          setView('myprofile');
        }}
        collectedStars={shopMapCollectedStars}
        onSelectShop={selectShopForMission}
        userMissions={userMissions}
      />
    );
  } else if (view === 'signup') {
    content = (
      <Signup
        onSwitchToLogin={() => setView('login')}
        onBack={() => setView('login')}
        onContinue={({ method, contact }) => {
          setPendingContact({ method, contact });
          setView('otp');
        }}
      />
    );
  } else if (view === 'otp') {
    content = (
      <Otp
        method={pendingContact.method}
        contact={pendingContact.contact}
        onBack={() => setView('signup')}
        onEdit={() => setView('signup')}
        onVerified={() => setView('name')}
      />
    );
  } else if (view === 'name') {
    content = (
      <NameStep
        onBack={() => setView('otp')}
        onContinue={() => setView('username')}
      />
    );
  } else if (view === 'username') {
    content = (
      <UsernameStep
        onBack={() => setView('name')}
        onContinue={() => setView('password')}
      />
    );
  } else if (view === 'password') {
    content = (
      <PasswordStep
        onBack={() => setView('username')}
        onContinue={() => setView('birthday')}
      />
    );
  } else if (view === 'birthday') {
    content = (
      <BirthdayStep
        onBack={() => setView('password')}
        onContinue={() => setView('gender')}
      />
    );
  } else if (view === 'gender') {
    content = (
      <GenderStep
        onBack={() => setView('birthday')}
        onContinue={() => setView('profile')}
      />
    );
  } else if (view === 'profile') {
    content = (
      <ProfileStep
        onBack={() => setView('gender')}
        onContinue={() => setView('login')}
        onSkip={() => setView('login')}
      />
    );
  } else if (view === 'map') {
    content = (
      <MissionMap
        currentStep={missionStep}
        onBack={() => setView('shopmap')}
        onSelectStep={(step) => {
          if (step === 1) setView('discover');
          if (step === 2) setView('selfie');
          if (step === 3) setView('post');
          if (step === 4) {
            setPointsReturnView('map');
            setView('points');
          }
        }}
      />
    );
  } else if (view === 'location') {
    content = (
      <Location
        onBack={() => setView('shopmap')}
        shopName={selectedShop?.name}
      />
    );
  } else if (view === 'discover') {
    content = (
      <Discover
        onBack={() => setView('shopmap')}
        cafeName={selectedShop?.name}
        selectedShop={selectedShop}
        onFound={(location) => {
          // currentUserMissionId can still be null here if startUserMission
          // (kicked off in selectShopForMission) hasn't resolved yet — never
          // silently skip reporting discovery in that case (that left real
          // missions permanently stuck on current_step 1, since nothing
          // ever retried it), and never advance to Selfie before the
          // backend has actually recorded it, so current_step is reliably
          // 2 by the time a selfie-completion request can be sent.
          if (currentUserMissionId == null) {
            window.alert('Your mission is not ready yet. Please go back and try again.');
            return;
          }

          completeMissionStep(currentUserMissionId, 1, location)
            .then(() => {
              setMissionStep(getStepNumber('selfie'));
              setView('selfie');
            })
            .catch((err) => {
              console.error('App: failed to report discover-step completion.', err);
              window.alert(err.message || 'Could not confirm discovery. Please try again.');
            });
        }}
      />
    );
  } else if (view === 'selfie') {
    content = (
      <Selfie
        onBack={() => setView('discover')}
        onOpenCamera={() => setView('camera')}
        onGallery={(photo) => {
          if (!photo) return;
          setPhotoToCrop(photo);
          setCropReturnView('selfie');
          setView('crop');
        }}
      />
    );
  } else if (view === 'camera') {
    content = (
      <Camera
        onBack={() => setView('selfie')}
        onCapture={(photo) => {
          setPhotoToCrop(photo);
          setCropReturnView('camera');
          setView('crop');
        }}
      />
    );
  } else if (view === 'crop') {
    content = (
      <Crop
        image={photoToCrop}
        onCancel={() => setView(cropReturnView)}
        onDone={(croppedPhoto) => {
          // cropReturnView records which screen led into Crop: 'camera' for
          // a just-captured photo, or 'selfie' (the gallery picker's host
          // screen) for a picked one — the real source of this photo.
          const source = cropReturnView === 'camera' ? 'camera' : 'gallery';

          if (currentUserMissionId == null) {
            window.alert('Your mission is not ready yet. Please go back and try again.');
            return;
          }

          uploadSelfie(currentUserMissionId, croppedPhoto, source)
            .then(() => {
              setSelfiePhoto(croppedPhoto);
              setPhotoToCrop(null);
              setMissionStep(getStepNumber('post'));
              setView('post');
            })
            .catch((err) => {
              console.error('App: failed to upload selfie.', err);
              window.alert(err.message || 'Could not save your selfie. Please try again.');
            });
        }}
      />
    );
  } else if (view === 'post') {
    content = (
      <Post
        photo={selfiePhoto}
        onBack={() => setView('selfie')}
        onPost={completePostStepAndAwardPoint}
        onSaveToGallery={completePostStepAndAwardPoint}
      />
    );
  } else if (view === 'points') {
    content = (
      <Points
        onBack={() => setView(pointsReturnView)}
        onContinue={() => {
          // Backend-confirmed (points()'s own journey_completed, Step 5F)
          // gates the Reward screen — never collectedStars/placesRequired
          // alone.
          if (journeyCompletedConfirmed) {
            setView('reward');
          } else {
            setView('shopmap');
          }
        }}
        // Star Badge count (Step 6E) is derived by Points itself from
        // userMissions/selectedJourneyId — never from the journey-scoped
        // `collectedStars` state, which can still read 0 right after
        // re-login until its own restore effect has run. totalStars keeps
        // using placesRequired as the existing denominator, unchanged.
        userMissions={userMissions}
        selectedJourneyId={selectedJourneyId}
        totalStars={placesRequired ?? 0}
      />
    );
  } else if (view === 'journey') {
    content = (
      <Journey
        onBack={() => setView('points')}
        onViewReward={() => setView('reward')}
        collectedStars={collectedStars}
        totalStars={placesRequired ?? 0}
      />
    );
  } else if (view === 'myprofile') {
    content = (
      <MyProfile
        onBack={() => setView(profileReturnView)}
        collectedStars={collectedStars}
        totalStars={placesRequired ?? 0}
        authUser={authUser}
        currentJourney={currentJourney}
        completedJourneys={completedJourneys}
        onViewAllJourneys={() => setView('journeyslist')}
        onSelectJourney={(journeyId) => {
          setSelectedJourneyId(journeyId);
          setJourneyDetailReturnView('myprofile');
          setView('journeydetail');
        }}
        onNavHome={() => setView('shopmap')}
        onNavJourney={() => setView('journeyslist')}
        onNavShops={() => setView('shopmap')}
      />
    );
  } else if (view === 'journeyslist') {
    content = (
      <JourneysList
        onBack={() => setView('myprofile')}
        onSelectJourney={(journeyId) => {
          setSelectedJourneyId(journeyId);
          setJourneyDetailReturnView('journeyslist');
          setView('journeydetail');
        }}
        onNavHome={() => setView('shopmap')}
        onNavShops={() => setView('shopmap')}
        onNavProfile={() => setView('myprofile')}
        userMissions={userMissions}
      />
    );
  } else if (view === 'journeydetail') {
    content = (
      <JourneyDetail
        journeyId={selectedJourneyId}
        onBack={() => setView(journeyDetailReturnView)}
        onSelectShop={selectShopForMission}
        userMissions={userMissions}
      />
    );
  } else if (view === 'reward') {
    content = <Reward onShowDeal={() => setView('scratch')} />;
  } else if (view === 'scratch') {
    content = (
      <ScratchCard
        onBack={() => setView('reward')}
        onContinue={() => {
          // Persist the reveal server-side (Step 5G) before advancing.
          // user_id/reward_id are never sent — the backend resolves both
          // from the authenticated user's own verified completed journey.
          // Idempotent: re-confirming an already-revealed reward is not an
          // error. Only on success does the UI move on; on failure the
          // user stays on this screen so they can retry, matching the
          // existing Post/Points failure-handling pattern.
          revealCompletedJourneyReward()
            .then(() => {
              setRewardCardsReturnView('shopmap');
              setView('rewardcards');
            })
            .catch((err) => {
              console.error('App: failed to persist the reward reveal.', err);
              window.alert(err.message || 'Could not confirm your reward. Please try again.');
            });
        }}
        reward={journeyReward}
        isLoading={journeyRewardStatus === 'loading'}
      />
    );
  } else if (view === 'rewardcards') {
    content = (
      <RewardCards
        onBack={() => setView('scratch')}
        onHome={() => setView(rewardCardsReturnView)}
        reward={journeyReward}
        isLoading={journeyRewardStatus === 'loading'}
      />
    );
  } else {
    // Default / 'home' view.
    content = <Home onStartMission={() => setView('login')} />;
  }

  // Force phone-width layout on every screen, desktop browsers included.
  return <div className="mobile-app-shell">{content}</div>;
}

export default App;

// $@Ymyn@me